//core
import React, { useState, useEffect, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { FieldHookConfig, useField, useFormikContext } from "formik";
import { Scrollbars } from "react-custom-scrollbars";
import { useDebounce } from "usehooks-ts";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Checkbox,
  AutocompleteCloseReason,
  createFilterOptions,
  Typography,
} from "@mui/material";

//icons
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import BlockIcon from '@mui/icons-material/Block';

//styles
import { useStyles } from "./styles";

//hooks
import useDidMountEffect from "../../../../customHooks/useDidMountEffect";

//components
import CircularProgress from "@mui/material/CircularProgress";
import {SelectFieldDataType} from "../../../../store/common/fields";
import {v4} from "uuid";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;


interface IMuiAutocompleteSelectProps  {
  keyValue: string;
  keyId: string;
  value: number | string;
  id: string;
  name: string;
  label: string;
  limitTags?: number;
  disabled?: boolean;
  multiple?: boolean;
  prefix?: string;
  data: SelectFieldDataType | null;
  isFetching: boolean;
  getOptions?: (page: number, search: string | null) => void;
  staticOptions?: any;
  showSelectAll?: boolean; // only for staticOptions & multiple
  onChangeDeals?: (value: any, setFieldValue: any) => void;
  labelWithId?: boolean;
  freeSolo?: boolean;
  creatable?: boolean;
  clearOnBlur?: boolean;
  isRequired?: boolean;
  onChangeCallback?: () => void;
}


const MuiAutocompleteSelect = (props: IMuiAutocompleteSelectProps & FieldHookConfig<string>) => {
  const { setFieldValue, setFieldTouched, validateField } = useFormikContext();
  const { t } = useTranslation();
  const styles = useStyles();

  const [field, meta] = useField(props);

  const {
    keyValue,
    keyId,
    id,
    value,
    name,
    label,
    disabled = false,
    multiple = false,
    limitTags = -1,
    prefix = '',
    staticOptions = null,
    showSelectAll = false,
    getOptions,
    isFetching,
    data,
    onChangeDeals,
    labelWithId = false,
    freeSolo = false,
    creatable = false,
    clearOnBlur = false,
    isRequired = false,
    onChangeCallback,
  } = props;

  const filter = createFilterOptions<any>();

  const [selectAll, setSelectAll] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<readonly any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [searchValue, setSearchValue] = useState('');
  const debouncedValue = useDebounce<string>(searchValue, 500);

  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!!staticOptions) {
      setOptions(staticOptions);
    }

    if (!data && open) {
      if (typeof getOptions === 'function') {
        getOptions(1, searchValue.length >= 2 ? searchValue : null);
      }
    }

    return () => {
      active = false;
    };
  }, [loading]);

  //if new staticOptions
  useEffect(() => {
    if (!!staticOptions) {
      setOptions(staticOptions);
    }
  }, [staticOptions]);

  useEffect(() => {
    if (!!data) {
      let newList = [...options, ...data?.list];

      if (showSelectAll && !!data && newList.length < 100 && newList.length > 0) {
        setOptions((prevState) => {
          const hasAll = newList.filter(item => item[`${keyId}`] === 'all').length;
          return [
            ...(!hasAll ? [{
              [`${keyId}`]: 'all',
              [`${keyValue}`]: 'all',
            }] : []),
            ...prevState,
            ...data?.list
          ]
        });
      } else {
        setOptions((prevState) => {
          return [...prevState, ...data?.list]
        });
      }

      const totalPages = data.total_pages ?? data.totalPages ?? 0;

      if (page < totalPages) {
        setHasMore(true)
      } else {
        setHasMore(false)
      }

      if (page === 1) {
        setSelectAll(false);
      }
    } else {
      setOptions([]);
      setHasMore(true);
    }
  }, [data]);

  useDidMountEffect(() => {
    setPage(1);
    setOptions([]);
    setHasMore(true);
    setSelectAll(false);

    setTimeout(() => {
      if (typeof getOptions === 'function') {
        getOptions(1, debouncedValue.length >= 2 ? debouncedValue : null);
      }
    }, 100);
  }, [debouncedValue]);

  useEffect(() => {
    return () => {
      setSearchValue('');
    }
  }, []);

  const checkAllChange = (selected: boolean) => {
    if (!!staticOptions) {
      if (!selectAll) {
        setFieldValue(field.name, staticOptions, true);
        setSelectAll(true);
      } else {
        setFieldValue(field.name, [], true);
        setSelectAll(false);
      }
    } else {
      if (!selected) {
        let uniqueOptions = [ ...value, ...options ]
          .filter((item) => item[keyId] !== 'all')
          .reduce((acc, item) => {
            if (acc.map[item[keyId]]) {
              return acc;
            }

            acc.map[item[keyId]] = true;
            acc.options.push(item);
            return acc;
          }, { map: {}, options: [] }).options;

        setFieldValue(field.name, [ ...uniqueOptions ], true);
        //setSelectAll(true);
      } else {
        setFieldValue(field.name, [], true);
        setSelectAll(false);
      }
    }
    setOpen(false);
  };

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const handleChangeInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        disablePortal={true}
        disableCloseOnSelect={multiple}
        id={id}
        size="small"
        multiple={multiple}
        value={value}
        open={open}
        limitTags={limitTags}
        disabled={disabled}
        freeSolo={freeSolo}
        {...(creatable ? {
          filterOptions: (options, params) => {
            const filtered = filter(options, params);

            const { inputValue } = params;

            // Suggest the creation of a new value
            if (creatable) {
              const isExisting = options.some((option) => inputValue === option[keyValue]);
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  [keyId]: inputValue,
                  [keyValue]: `${t("common.buttons.add")} "${inputValue}"`,
                });
              }
            }

            return filtered;
          }
        } : {
          filterOptions: (options, state) => options,
        })}
        noOptionsText={t("common.components.autocomplete.no_matches")}
        loadingText={t("common.components.autocomplete.loading")}
        onOpen={handleOpen}
        onClose={(e: any, reason: AutocompleteCloseReason) => {
          setOpen(false);
          setSearchValue('');
        }}
        loading={isFetching}
        blurOnSelect={false}
        clearOnBlur={clearOnBlur}
        options={options.map((option) => option)}
        isOptionEqualToValue={(option, value) => option[keyId] === value[keyId]}
        getOptionDisabled={(option) => option.hasOwnProperty('status') && option?.status === 'blocked'}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }

          if (option.inputValue) {
            return option.inputValue;
          }

          if (!!prefix) {
            return !!option[keyValue] ? labelWithId ? `${option[keyId]}-${t(`${prefix}${option[keyValue]}`)}` : t(`${prefix}${option[keyValue]}`) : '';
          } else {
            return !!option[keyValue] ? labelWithId ? `${option[keyId]}-${option[keyValue]}` : option[keyValue] : '';
          }
        }}
        renderOption={(props, option, { selected }) => {
          if (multiple) {
            if (option[keyId] === 'all') {
              return (
                <Box
                  sx={{ minHeight: 'auto' }}
                  component="li"
                  {...props}
                  className={styles.selectAll}
                  // key={`${name}-li-${option[keyId]}`}
                  key={v4()}
                  onClick={() => checkAllChange(selectAll)}
                >
                  <Checkbox
                    sx={{ padding: '0', width: 24, height: 24, marginRight: 1 }}
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selectAll}
                  />
                  {selectAll ? t("common.components.select_all.clear") : t("common.components.select_all.check")}
                </Box>
              )
            } else {
              return (
                <Box
                  sx={{
                    minHeight: '36px!important',
                    padding: '6px 16px!important'
                  }}
                  component="li" {...props}
                  // key={`${name}-li-${option[keyId]}`}
                  key={v4()}
                >
                  <Checkbox
                    sx={{ padding: '0', width: 24, height: 24, marginRight: 1, flexShrink: 0 }}
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {prefix
                    ? labelWithId ? `${option[keyId]}-${t(`${prefix}${option[keyValue]}`)}` : t(`${prefix}${option[keyValue]}`)
                    : labelWithId ? `${option[keyId]}-${option[keyValue]}` : option[keyValue]
                  }
                </Box>
              )
            }
          } else {
            return (
              <Box
                sx={{
                  minHeight: '36px!important',
                  padding: '6px 16px!important',
                }}
                component="li"
                {...props}
                // key={`${name}-li-${option[keyId]}`}
                key={v4()}
              >
                <Box component='span' className={styles.optionContent}>
                  <Box component='span' className={styles.optionLabel}>
                    {prefix
                      ? labelWithId ? `${option[keyId]}-${t(`${prefix}${option[keyValue]}`)}` : t(`${prefix}${option[keyValue]}`)
                      : labelWithId ? `${option[keyId]}-${option[keyValue]}` : option[keyValue]
                    }
                  </Box>
                  {option.hasOwnProperty('status') && option?.status === 'blocked' && (
                    <Box className={styles.blocked} component="span">
                      <BlockIcon fontSize="small" />
                      <Typography variant="caption" style={{ whiteSpace: 'nowrap' }}>
                        {t("common.components.partners_status.blocked")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )
          }
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.filter(item => item[keyId] !== 'all').map((option, index) => (
            <Chip
              size="small"
              variant="filled"
              label={prefix
                ? labelWithId ? `${option[keyId]}-${t(`${prefix}${option[keyValue]}`)}` : t(`${prefix}${option[keyValue]}`)
                : labelWithId ? `${option[keyId]}-${option[keyValue]}` : option[keyValue]
              }
              {...getTagProps({ index })}
              sx={{ maxWidth: '200px!important' }}
              key={`${option[keyId]}-${index}`}
            />
          ))
        }
        onChange={(e, val, reason) => {
          if (!!onChangeDeals) onChangeDeals(val, setFieldValue);
          if (reason === 'clear') {
            setSelectAll(false);
          }

          if (typeof val === 'string') {
            setFieldValue(
              field.name,
              {
                [keyId]: val,
                [keyValue]: val,
              },
              true
            );
          } else if (creatable && !!val && val.hasOwnProperty('inputValue')) {
            setFieldValue(
              field.name,
              {
                [keyId]: val[keyId],
                [keyValue]: val.inputValue,
              },
              true
            );
          } else {
            setFieldValue(field.name, val, true);
          }

          if (typeof onChangeCallback === 'function') {
            onChangeCallback();
          }

          setTimeout(() => {
            validateField(field.name);
          }, 50)
        }}
        //inputValue={searchValue}
        onInputChange={(event, val, reason) => {
          if (event && event.type === 'blur' ) {
            setSearchValue('');
            setSelectAll(false);
          } else if ( reason !== 'reset' ) {
            setSearchValue(val);
          }
        }}
        onBlur={(e) => {
          setFieldTouched(field.name, true, true);
          setSearchValue('');
        }}
        componentsProps={{
          paper: {
            elevation: 8,
            className: !!options.filter((item) => item.id === 'all').length ? styles.withSelectAll : '',
          }
        }}
        ListboxProps={{
          style: { maxHeight: MAX_HEIGHT },
          role: "list-box",
          onScroll: ({ currentTarget }) => {
            const scrollPosition = currentTarget.scrollTop + currentTarget.clientHeight;

            if (currentTarget.scrollHeight - scrollPosition <= 1 && hasMore) {
              if (typeof getOptions === 'function') {
                setPage(page + 1);
                getOptions(page + 1, searchValue);
              }
            }
          },
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              name={name}
              label={`${label}${isRequired ? '*' : ''}`}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error ? meta.error : ''}
              placeholder={t("common.components.autocomplete.placeholder")}
              InputProps={{
                ...params.InputProps,
                sx: {
                  paddingRight: creatable ? '39px!important' : '0',
                },
                startAdornment: value !== null && !!value.length ? (
                  <Box
                    sx={{
                      paddingRight: multiple && !!value.length ? '30px' : '0px',
                      boxSizing: 'initial',
                    }}
                    onClick={() => {
                      if (!disabled) {
                        setOpen(true);
                      }
                    }}
                  >
                    <Scrollbars
                      className={styles.customScroll}
                      autoHide={true}
                      hideTracksWhenNotNeeded={true}
                      autoHeight={true}
                      autoHeightMin={28}
                      autoHeightMax={84}
                      renderView={props => <div {...props} className="view"/>}
                      renderTrackHorizontal={props => <div {...props} style={{display: 'none'}} className="track-horizontal"/>}
                      renderTrackVertical={props => <div {...props} className="track-vertical"/>}
                    >
                      {params.InputProps.startAdornment}
                    </Scrollbars>
                  </Box>
                ) : undefined,
                endAdornment: (
                  <React.Fragment>
                    {isFetching ? <CircularProgress sx={{ position: 'absolute', right: '11px' }} color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              onChange={(e) => handleChangeInput(e)}
            />
          )
        }}
      />
    </Box>
  );
};


export default MuiAutocompleteSelect;
